// packages/ui-kit/src/primitives/ConfirmDialog.tsx
// C3F v18 — reference: design/system/components/feedback/ConfirmDialog.{jsx,prompt.md}
import type { ReactNode } from "react";
import { Modal } from "./Modal.js";
import { Button } from "./Button.js";

interface Props {
  open: boolean;
  /** DS: the title says what happens, never "Sei sicuro?". */
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** `danger` makes the confirm button red (destructive actions). */
  tone?: "default" | "danger";
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * A small Modal-based yes/no confirmation — the unified replacement for
 * `window.confirm` (localizable, on-theme, accessible, animated). The caller
 * owns the `open` state; `onCancel` also fires on overlay click / Escape.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Conferma",
  cancelLabel = "Annulla",
  tone = "default",
  busy = false,
  onConfirm,
  onCancel,
}: Props) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      footer={
        <>
          {/* DS: cancel is the default (secondary) Button, confirm closes the intention. */}
          <Button
            type="button"
            variant="secondary"
            className="w-full sm:w-auto"
            onClick={onCancel}
            disabled={busy}
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={tone === "danger" ? "danger" : "primary"}
            className="w-full sm:w-auto"
            onClick={onConfirm}
            loading={busy}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {message && <div className="text-sm text-fg-muted">{message}</div>}
    </Modal>
  );
}
